/**
 * The profile phone: a country picked from a list plus the number typed after
 * it, stored as one international number (`+919876543210`, E.164), so every
 * saved number carries its country.
 *
 * The rules — each country's calling code and valid number lengths — come from
 * libphonenumber-js, Google's phone-number metadata. The backend checks with
 * the same library, so the two cannot disagree about what is valid.
 */

import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js'

export type { CountryCode }

/** What the picker starts on for a profile with no phone yet. */
export const DEFAULT_PHONE_COUNTRY: CountryCode = 'IN'

export interface PhoneCountry {
  iso: CountryCode
  /** `+91` */
  dialCode: string
  /** In the app's language where the browser knows it, else English. */
  name: string
}

export const dialCodeOf = (iso: CountryCode): string => `+${getCountryCallingCode(iso)}`

/** Every country the phone rules know, named in `language`, sorted by name. */
export function phoneCountries(language: string): PhoneCountry[] {
  let names: Intl.DisplayNames | undefined
  try {
    names = new Intl.DisplayNames([language, 'en'], { type: 'region' })
  } catch {
    // A browser without Intl.DisplayNames lists the ISO codes instead.
  }
  return getCountries()
    .map((iso) => ({ iso, dialCode: dialCodeOf(iso), name: names?.of(iso) ?? iso }))
    .sort((a, b) => a.name.localeCompare(b.name, language))
}

/**
 * A stored number split for editing: its country, and the rest as digits
 * without the country's trunk prefix (`+442079460958` -> GB, `2079460958`).
 * A value that is not an international number — an old local copy — keeps
 * its text under the default country, for the user to correct.
 */
export function splitPhone(stored: string): { country: CountryCode; national: string } {
  const parsed = stored ? parsePhoneNumberFromString(stored) : undefined
  if (parsed) {
    // Some numbers fit no single country of a shared code (+1 is the US,
    // Canada and the Caribbean); those take the code's first country.
    const country =
      parsed.country ?? getCountries().find((c) => getCountryCallingCode(c) === parsed.countryCallingCode)
    if (country) return { country, national: parsed.nationalNumber }
  }
  return { country: DEFAULT_PHONE_COUNTRY, national: stored }
}

/**
 * The picked country and the typed number as one international number.
 * '' when nothing was typed (the phone is optional); null when it is not a
 * valid number for that country — the wrong length for it, or text that is not
 * a number at all. A number typed with its own `+` code wins over the picked
 * country.
 */
export function joinPhone(country: CountryCode, national: string): string | null {
  if (!national.trim()) return ''
  const parsed = parsePhoneNumberFromString(national, { defaultCountry: country, extract: false })
  return parsed?.isValid() ? parsed.number : null
}

/** A stored number for display: `+91 98765 43210`. Shown as stored if it does not parse. */
export function formatPhone(stored: string): string {
  if (!stored) return ''
  return parsePhoneNumberFromString(stored)?.formatInternational() ?? stored
}
